import React, { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { formatShopifyFieldValue, type FormattedValue } from "../../../utils/shopifyFormatting";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation, getUITranslation } from "@/lib/views/labels/translations";
import { Switch } from "@/components/ui/switch";
import { ChildTablePreview } from "@/components/child-table/ChildTablePreview";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useDetailEditMode } from "../../../../hooks/useDetailEditMode";
import { useInlineFieldSave } from "@/components/inline-edit/detail/useInlineFieldSave";
import { useBufferFieldSave } from "@/components/inline-edit/detail/useBufferFieldSave";
import { useInlineFieldUndo } from "@/components/inline-edit/detail/useInlineFieldUndo";
import { getAdjacentField } from "@/components/inline-edit/detail/getAdjacentField";
import { InlineFieldRow } from "../../../InlineFieldRow";
import type { DocFieldSchema } from "@/lib/erp/meta";

/**
 * Helper to render FormattedValue (handles both string and HTML content)
 */
function renderFormattedValue(value: FormattedValue): React.ReactNode {
  if (typeof value === "object" && "__html" in value) {
    return <span dangerouslySetInnerHTML={value} />;
  }
  return <>{value}</>;
}

/**
 * Item Price Basic Section Widget
 *
 * Custom widget that forces all fields into a single group.
 * Supports both modal (default) and inline editing modes.
 */
export function ItemPriceBasicSection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  isSaving,
  detailKey,
  fieldErrors,
  onClearFieldError,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [modalField, setModalField] = useState<DocFieldSchema | null>(null);
  const { rowPx, rowPy, rowFirst, rowGap, dividerMx, rowLabel } = useDetailDensity();

  // Edit mode: mobile forces modal (built into hook)
  const { editMode } = useDetailEditMode();
  const isNew = !formData?.name;

  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  // Inline save: network patch for existing docs, local buffer for new docs
  const resolvedDetailKey = detailKey ?? [doctype.toLowerCase(), formData?.name ?? ""];
  const inlineSave = useInlineFieldSave({
    doctype,
    docname: formData?.name ?? "",
    detailKey: resolvedDetailKey,
  });
  const bufferSave = useBufferFieldSave();
  const { saveField, getFieldState, retryField, undoField, getUndoable, lastSavedField } = isNew ? bufferSave : inlineSave;

  useInlineFieldUndo({ undoField, getUndoable, lastSavedField });

  // Translate section label
  const translatedSectionLabel = useMemo(() => {
    return section.label ? getSectionTitleTranslation(section.label, language) : null;
  }, [section.label, language]);

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      editFields: getUI("modal_edit_fields", "Edit Fields"),
    };
  }, [language]);

  // Get all fields from section columns
  const allFields = useMemo(
    () => section.columns.flatMap((col) => col.fields),
    [section.columns]
  );

  // Compute field dependencies
  const { visibleFieldNames, fieldRequired } = useFieldDependencies(allFields, formData);

  const filteredFields = allFields
    .filter((field) => visibleFieldNames.has(field.fieldname))
    .filter((field) => field.fieldname !== 'name');

  const currency = formData.currency || 'THB';

  // Force all fields into a single group
  const singleGroup = useMemo(() => ({
    key: "single-group",
    label: translatedSectionLabel || uiLabels.editFields,
    fields: filteredFields,
  }), [filteredFields, translatedSectionLabel, uiLabels.editFields]);

  if (singleGroup.fields.length === 0) return null;

  const handleInlineCommit = async (fieldname: string, newValue: unknown) => {
    const previousValue = formData[fieldname];
    setEditingField(null);
    onFieldChange(fieldname, newValue);
    await saveField(fieldname, newValue, previousValue);
  };

  const handleAdvance = useCallback((fieldname: string, direction: "next" | "prev") => {
    const next = getAdjacentField(singleGroup.fields, fieldname, direction);
    if (next) setEditingField(next);
  }, [singleGroup.fields]);

  return (
    <>
      <div className="space-y-2">
        <div
          className={cn(
            CARD,
            editMode === "modal" && !disabled && "cursor-pointer",
            disabled && "opacity-60 cursor-not-allowed"
          )}
          onClick={
            editMode === "modal"
              ? () => { if (!disabled) setModalOpen(true); }
              : undefined
          }
        >
          <div className="space-y-0">
            {singleGroup.fields.map((field, index) => {
              const value = formData[field.fieldname];
              const isTable = field.fieldtype === 'Table' || field.fieldtype === 'Table MultiSelect';
              const isRequired = fieldRequired[field.fieldname] ?? field.reqd === 1;
              const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
              const isFirst = index === 0;
              const isLast = index === singleGroup.fields.length - 1;

              if (editMode === "inline") {
                return (
                  <InlineFieldRow
                    key={field.fieldname}
                    field={field}
                    value={value}
                    label={resolvedLabel.label}
                    editMode={editMode}
                    isEditing={editingField === field.fieldname}
                    onStartEdit={() => setEditingField(field.fieldname)}
                    onCommit={(v) => handleInlineCommit(field.fieldname, v)}
                    onCancel={() => setEditingField(null)}
                    saveState={getFieldState(field.fieldname)}
                    currency={currency}
                    tooltip={field.description}
                    fullWidth={isTable}
                    onModalClick={isTable ? () => setModalField(field) : undefined}
                    onAdvance={(dir) => handleAdvance(field.fieldname, dir)}
                    onRetry={() => retryField(field.fieldname)}
                    error={fieldErrors?.[field.fieldname]}
                    onClearError={() => onClearFieldError?.(field.fieldname)}
                    rowPx={rowPx}
                    rowPy={isFirst ? rowFirst : rowPy}
                    rowGap={rowGap}
                    rowLabel={rowLabel}
                    dividerMx={dividerMx}
                    isLast={isLast}
                    isRequired={isRequired}
                  >
                    {isTable && (
                      <ChildTablePreview field={field} value={value} currency={currency} maxRows={3} maxFields={3} />
                    )}
                  </InlineFieldRow>
                );
              }

              // Modal mode: existing rendering
              const isCheck = field.fieldtype === 'Check';
              let displayValue: React.ReactNode;
              let formattedValue: FormattedValue | null = null;
              if (isTable) {
                displayValue = (
                  <ChildTablePreview field={field} value={value} currency={currency} maxRows={3} maxFields={3} />
                );
              } else if (isCheck) {
                displayValue = (
                  <Switch checked={value === 1 || value === true} disabled className="pointer-events-none" />
                );
              } else {
                formattedValue = formatShopifyFieldValue(field, value, currency);
              }

              return (
                <React.Fragment key={field.fieldname}>
                  <div
                    className={cn(
                      isTable ? "flex flex-col gap-3" : `flex items-center ${rowGap}`,
                      rowPx,
                      isFirst ? rowFirst : rowPy,
                    )}
                  >
                    <span className={cn(
                      rowLabel, "shrink-0",
                      isTable ? "w-full" : "w-1/2"
                    )} style={{ color: 'rgb(17 24 39 / var(--tw-text-opacity, 1))' }}>
                      {resolvedLabel.label}
                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    <div className={cn(
                      "text-foreground",
                      isTable ? "w-full" : `${rowLabel} w-1/2 text-right flex items-center justify-end`
                    )}>
                      {isTable || isCheck ? displayValue : <span className="break-words whitespace-pre-wrap">{renderFormattedValue(formattedValue!)}</span>}
                    </div>
                  </div>
                  {!isLast && (
                    <div className={cn(dividerMx, "border-t border-border/40")} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={translatedSectionLabel || section.label || "Edit Fields"}
        fields={singleGroup.fields}
        section={undefined}
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={onSubmit}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
        documentName={formData?.name}
      />

      {/* Per-field modal for Table fields in inline mode */}
      {modalField && (
        <MetaFieldEditModal
          open={true}
          onOpenChange={(open) => { if (!open) setModalField(null); }}
          title={resolveLabel(doctype, modalField, mode, style, enableSystemTranslations).label}
          fields={[modalField]}
          section={undefined}
          formData={formData}
          onFieldChange={onFieldChange}
          onSubmit={onSubmit}
          onSave={onSave}
          disabled={disabled}
          isSaving={isSaving}
          documentName={formData?.name}
        />
      )}
    </>
  );
}
